import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { authenticate } from "../../../service-layer/AuthenticationService";
import { postDoctorRequest } from "../../../service-layer/MedicalStaffService";
import { toast, ToastContainer } from "react-toastify";
import { sendEmail } from "../../../service-layer/EmailService";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import "./LoginForm.css";
import Navbar from "../../components/NavBar";

import { useStateContext } from "../../../contextProvider";

const LoginForm = () => {

  const {
    selectedPatient, setSelectedPatient, selectedPatientData, 
    setSelectedPatientData, doctorPage, setDoctorPage, setAdminPage
  } = useStateContext()

  const { t, i18n } = useTranslation();

  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [fiscalCode, setFiscalCode] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [touchedLoginEmail, setTouchedLoginEmail] = useState(false);
  const [touchedLoginPassword, setTouchedLoginPassword] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePasswordsMatch = () => {
    return signupPassword === repeatPassword;
  };

  const validatePasswordStrength = (password) => {
    const minLength = /.{8,}/;
    const hasUpperCase = /[A-Z]/;
    const hasLowerCase = /[a-z]/;
    const hasDigit = /\d/;
    const hasSpecialChar = /[!@#$%^&*]/;

    return (
      minLength.test(password) &&
      hasUpperCase.test(password) &&
      hasLowerCase.test(password) &&
      hasDigit.test(password) &&
      hasSpecialChar.test(password)
    );
  };

  const extractSurnameCode = (surname) => {
    // Remove spaces, apostrophes, and consider the surname as a continuous string
    const cleanSurname = surname.replace(/[\s'"]/g, "").toUpperCase();
    const consonants = cleanSurname.replace(/[AEIOU]/g, ""); // Remove vowels to get consonants
    const vowels = cleanSurname.replace(/[^AEIOU]/g, ""); // Remove consonants to get vowels

    if (consonants.length >= 3) {
      return consonants.slice(0, 3); // Take the first three consonants
    } else if (consonants.length === 2) {
      return consonants + vowels.charAt(0); // Two consonants and first vowel
    } else if (consonants.length === 1) {
      return consonants + vowels.slice(0, 2); // One consonant and first two vowels
    } else {
      return vowels.slice(0, 2) + "X"; // Only vowels, add 'X' for missing character
    }
  };

  const extractNameCode = (name) => {
    // Remove spaces, apostrophes, and consider the name as a continuous string
    const cleanName = name.replace(/[\s'"]/g, "").toUpperCase();
    const consonants = cleanName.replace(/[AEIOU]/g, ""); // Remove vowels to get consonants
    const vowels = cleanName.replace(/[^AEIOU]/g, ""); // Remove consonants to get vowels

    if (consonants.length >= 4) {
      return consonants.charAt(0) + consonants.charAt(2) + consonants.charAt(3); // First, third, fourth consonant
    } else if (consonants.length === 3) {
      return consonants; // All three consonants
    } else if (consonants.length === 2) {
      return consonants + vowels.charAt(0); // Two consonants and first vowel
    } else if (consonants.length === 1) {
      return consonants + vowels.slice(0, 2); // One consonant and first two vowels
    } else {
      return vowels.slice(0, 2) + "X"; // Only vowels, add 'X' for missing character
    }
  };

  const validateFiscalCode = (code) => {
    const fiscalCodeRegex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;

    if (!fiscalCodeRegex.test(code)) {
      return false;
    }

    // Extract the expected characters from name and surname
    const surnameCode = extractSurnameCode(surname);
    const nameCode = extractNameCode(name);
    return code.slice(0, 3) === surnameCode && code.slice(3, 6) === nameCode;
  };

  const handleBlurLoginEmail = () => {
    setTouchedLoginEmail(true);
  };

  const handleBlurLoginPassword = () => {
    setTouchedLoginPassword(true);
  };

  const homePath =
    localStorage.authority === "ADMIN" ? "/dashboard_admin" : "/patients";

  useEffect(() => {
    if (window.localStorage.getItem("auth_token") !== null) navigate(homePath);
  }, [navigate]);

  const handleSubmitLogin = (event) => {
    authenticate(loginEmail, loginPassword).then((response) => {
      if (response?.status === 200) {
        console.log("these are the authentication data");
        console.log(response.data);

        // Store authentication data
        window.localStorage.setItem("name", response.data.name);
        window.localStorage.setItem("surname", response.data.surname);
        window.localStorage.setItem("authority", response.data.authority);

        // Handle post-login redirect
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
        } else {
          // Default redirect logic
          if (response.data.authority === "ADMIN"){
            setAdminPage('dashboard')
            navigate("/dashboard_admin", { currentMedicalStaff: response.data });
          }
          if (response.data.authority === "DOCTOR"){
            navigate("/patients", { currentMedicalStaff: response.data });
            setAdminPage(undefined)
          }
        }

        setSelectedPatient(undefined)
        setSelectedPatientData(undefined)
        setDoctorPage(undefined)

      } else if (response?.status === 403) {
        setLoginEmail("");
        setLoginPassword("");
        toast.error(t("authentication_failed"), {
          position: "top-center",
        });
      } else {
        console.log("something else happened");
        toast.error(t("something_wrong"), {
          position: "top-center",
        });
      }
    });
  };

  const handleSubmitSignup = (event) => {
    event.preventDefault();
    setFormSubmitted(true);

    if (
      name.trim() === "" ||
      surname.trim() === "" ||
      fiscalCode.trim() === "" ||
      signupEmail.trim() === "" ||
      signupPassword.trim() === "" ||
      repeatPassword.trim() === "" ||
      registrationNumber.trim() === "" ||
      specialization.trim() === "" ||
      !isPrivacyPolicyChecked
    ) {
      alert(t("fill_required_fields"));
      return;
    }

    if (signupEmail.trim() !== "" && !validateEmail(signupEmail)) {
      return;
    }

    if (!validateFiscalCode(fiscalCode)) {
      alert(t("fiscal_code_invalid"));
      return;
    }

    if (!validatePasswordsMatch()) {
      alert(t("passwords_do_not_match"));
      return;
    }

    if (!validatePasswordStrength(signupPassword)) {
      alert(t("password_not_secure"));
      return;
    }

    const currentTimestamp = new Date().toISOString();

    const request = {
      name: name,
      surname: surname,
      fiscalCode: fiscalCode,
      password: signupPassword,
      email: signupEmail,
      timestamp: currentTimestamp,
      requestState: "pending",
      registrationNumber: registrationNumber,
      specialization: specialization,
    };

    postDoctorRequest(request)
      .then((response) => {
        if (response.status === 200) {
          toast.success(t("request_processing"), {
            position: "top-center",
          });
          console.log(signupEmail);
          sendEmail(
            signupEmail,
            t("subject_request_received"),
            t("body_request_received"),
          ).then((r) => console.log(r));
          setName("");
          setSurname("");
          setFiscalCode("");
          setSignupEmail("");
          setSignupPassword("");
          setRepeatPassword("");
          setRegistrationNumber("");
          setSpecialization("");
          setFormSubmitted(false);
        } else {
          toast.error(t("something_wrong"), {
            position: "top-center",
          });
        }
      })
      .catch((error) => {
        console.error("Error submitting request:", error);
        toast.error(t("something_wrong"));
      });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const languages = [
    { code: "en", name: t("english") },
    { code: "it", name: t("italian") },
  ];

  useEffect(() => {
    changeLanguage("it"); // Set the default language to Italian
  }, []);

  return (
    <div className="login-page">
      <div className="d-flex">
        <div
          style={{
            flex: "1 1 auto",
            display: "flex",
            flexFlow: "column",
            height: "100vh",
            overflowY: "hidden",
          }}
        >
          <Navbar />
          <div style={{ height: "100%" }}>
            <div className="login-container">
              <ToastContainer
                position="top-center"
                className="Toastify__toast-container--center"
              />
              <div className={`form-container ${isLogin ? "login" : "signup"}`}>
                <div className="form-toggle">
                  <div
                    onClick={() => setIsLogin(true)}
                    className={`toggle-item ${isLogin ? "active" : ""}`}
                  >
                    {t("login")}
                  </div>
                  <div
                    onClick={() => setIsLogin(false)}
                    className={`toggle-item ${!isLogin ? "active" : ""}`}
                  >
                    {t("signup")}
                  </div>
                </div>
                <form
                  className="form"
                  onSubmit={isLogin ? handleSubmitLogin : handleSubmitSignup}
                >
                  {!isLogin ? (
                    <>
                      <h2>{t("signup_form")}</h2>
                      <input
                        type="text"
                        placeholder={t("name")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && name.trim() === "" ? "error" : ""}`}
                      />
                      {formSubmitted && name.trim() === "" && (
                        <p className="error-text">{t("name_required")}</p>
                      )}
                      <input
                        type="text"
                        placeholder={t("surname")}
                        value={surname}
                        onChange={(e) => setSurname(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && surname.trim() === "" ? "error" : ""}`}
                      />
                      {formSubmitted && surname.trim() === "" && (
                        <p className="error-text">{t("surname_required")}</p>
                      )}
                      <input
                        type="text"
                        placeholder={t("fiscal_code")}
                        value={fiscalCode}
                        onChange={(e) => {
                          // Convert the input value to uppercase and set it
                          const upperCaseValue = e.target.value.toUpperCase();
                          setFiscalCode(upperCaseValue);
                        }}
                        required
                        className={`input-field ${formSubmitted && (fiscalCode.trim() === "" || !validateFiscalCode(fiscalCode)) ? "error" : ""}`}
                      />
                      {formSubmitted && fiscalCode.trim() === "" && (
                        <p className="error-text">
                          {t("fiscal_code_required")}
                        </p>
                      )}
                      {formSubmitted &&
                        fiscalCode.trim() !== "" &&
                        !validateFiscalCode(fiscalCode) && (
                          <p className="error-text">
                            {t("fiscal_code_invalid")}
                          </p>
                        )}

                      <input
                        type="email"
                        placeholder={t("email_address")}
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && !validateEmail(signupEmail) ? "error" : ""}`}
                      />
                      {formSubmitted && !validateEmail(signupEmail) && (
                        <p className="error-text">{t("email_not_valid")}</p>
                      )}

                      <input
                        type="registration_number"
                        placeholder={t("registration_number")}
                        value={registrationNumber}
                        onChange={(e) => setRegistrationNumber(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && registrationNumber.trim() === "" ? "error" : ""}`}
                      />

                      <input
                        type="speicialization"
                        placeholder={t("specialization")}
                        value={specialization}
                        onChange={(e) => setSpecialization(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && specialization.trim() === "" ? "error" : ""}`}
                      />

                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("password")}
                        value={signupPassword}
                        onChange={(e) => setSignupPassword(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && !validatePasswordStrength(signupPassword) ? "error" : ""}`}
                      />
                      {formSubmitted &&
                        !validatePasswordStrength(signupPassword) && (
                          <p className="error-text">
                            {t("password_not_secure")}
                          </p>
                        )}
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("repeat_password")}
                        value={repeatPassword}
                        onChange={(e) => setRepeatPassword(e.target.value)}
                        required
                        className={`input-field ${formSubmitted && repeatPassword !== signupPassword ? "error" : ""}`}
                      />
                      {formSubmitted && repeatPassword !== signupPassword && (
                        <p className="error-text">
                          {t("passwords_do_not_match")}
                        </p>
                      )}

                      <div className="privacy-policy-section">
                        <input
                          type="checkbox"
                          checked={isPrivacyPolicyChecked}
                          onChange={(e) =>
                            setIsPrivacyPolicyChecked(e.target.checked)
                          }
                        />
                          <span className="privacy-policy-text">
                              <a href="/privacy_policy" target="_blank" rel="noopener noreferrer">
                                {t("i_have_read_and_agree_to_the_privacy_policy")}
                              </a>
                        </span>
                          {formSubmitted && !isPrivacyPolicyChecked && (
                              <p className="error-text">
                              {t("please_read_and_agree_to_the_privacy_policy")}
                          </p>
                        )}
                      </div>

                      <div className="form-button" onClick={handleSubmitSignup}>
                        {t("signup")}
                      </div>
                    </>
                  ) : (
                    <>
                      <h2>{t("login_form")}</h2>
                      <input
                        type="email"
                        placeholder={t("email_address")}
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        onBlur={handleBlurLoginEmail}
                        required
                        className={`input-field ${touchedLoginEmail && !validateEmail(loginEmail) ? "error" : ""}`}
                      />
                      {touchedLoginEmail && !validateEmail(loginEmail) && (
                        <p className="error-text">{t("email_not_valid")}</p>
                      )}
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder={t("password")}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        onBlur={handleBlurLoginPassword}
                        required
                        className={`input-field ${touchedLoginPassword && loginPassword.trim() === "" ? "error" : ""}`}
                      />
                      {touchedLoginPassword && loginPassword.trim() === "" && (
                        <p className="error-text">{t("password_required")}</p>
                      )}
                      <div className="form-button" onClick={handleSubmitLogin}>
                        {t("login")}
                      </div>
                    </>
                  )}
                </form>
                {isLogin ? (
                  <>
                    <p style={{  color: "#ff0000", textAlign: "center", padding: "0px", margin: "4px" }}>
                      {t("not_a_member")}{" "}
                      <span className="link-style"  onClick={() => setIsLogin(false)}>
                        {t("signup_now")}
                      </span>
                    </p>
                    <NavLink
                      className="centered-link"
                      to="/request_password_reset"
                    >
                      {t("forgot_password")}
                    </NavLink>
                  </>
                ) : (
                  <>
                    <p style={{  color: "#ff0000", textAlign: "center", padding: "0px", margin: "4px" }}>
                      {t("already_a_member")}{" "}
                      <span className="link-style"  onClick={() => setIsLogin(true)}>
                        {t("login_now")}
                      </span>
                    </p>
                    <NavLink
                      className="centered-link"
                      to="/request_password_reset"
                    >
                      {t("forgot_password")}
                    </NavLink>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
